import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  insightsHeader,
  Octopus,
  RelatedEntryRelationship,
} from '@maximizer/core/shared/domain';
import { Observable, map } from 'rxjs';
import { ContextService } from './context.service';

@Injectable()
export class RelatedEntryService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  getRelatedEntries(key: string): Observable<RelatedEntryRelationship[]> {
    const request: Octopus.RelatedEntryReadRequest = {
      RelatedEntry: {
        Criteria: {
          SearchQuery: {
            FromKey: {
              $EQ: key,
            },
          },
        },
        Scope: {
          Fields: {
            Key: 1,
            FromKey: 1,
            ToKey: 1,

            RelationType: {
              Value: 1,
              DisplayValue: 1,
            },
          },
        },
      },
    };

    return this.http
      .post<Octopus.RelatedEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.RelatedEntryMapper();
            return mapper.from(result.RelatedEntry.Data);
          }
          return [];
        }),
      );
  }

  getRelatedByRelationTypes(
    key: string,
    relationTypes: string[],
    udfKeys: string[],
  ): Observable<Octopus.RelatedEntry[]> {
    return this.getRelatedEntriesByRelationTypes(
      key,
      relationTypes,
      udfKeys,
      true,
    );
  }

  getRelatedNotInRelationTypes(
    key: string,
    relationTypes: string[],
    udfKeys: string[],
  ): Observable<Octopus.RelatedEntry[]> {
    return this.getRelatedEntriesByRelationTypes(
      key,
      relationTypes,
      udfKeys,
      false,
    );
  }

  private getRelatedEntriesByRelationTypes(
    key: string,
    relationTypes: string[],
    udfKeys: string[],
    include: boolean,
  ): Observable<Octopus.RelatedEntry[]> {
    const query: Octopus.SearchQuery<Octopus.RelatedEntry> = {
      $AND: [
        {
          FromKey: {
            $EQ: {
              Body: key,
              OrRelated: true,
            },
          },
        },
        {
          RelationType: include
            ? { $IN: relationTypes }
            : { $NIN: relationTypes },
        },
      ],
    };

    const request: Octopus.RelatedEntryReadRequest = {
      RelatedEntry: {
        Criteria: { SearchQuery: query },
        Scope: {
          Fields: {
            Key: 1,
            FromKey: 1,
            ToKey: 1,
            RelationType: {
              Value: 1,
              DisplayValue: 1,
            },
            To: {
              ParentKey: 1,
              FullName: 1,
              FirstName: 1,
              LastName: 1,
              Type: 1,
              CompanyName: 1,
            },
          },
        },
      },
      Compatibility: { AbEntryKey: '2.0' },
    };

    udfKeys.forEach((udfKey) => {
      (request.RelatedEntry.Scope.Fields.To as Octopus.Entity)[udfKey] = 1;
    });

    return this.http
      .post<Octopus.RelatedEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.RelatedEntry?.Data ?? [];
          }
          return [];
        }),
      );
  }

  createRelatedEntry(
    fromKey: string,
    toKey: string,
    relationType: string,
  ): Observable<Octopus.RelatedEntry[]> {
    const request: Octopus.RelatedEntryWriteRequest = {
      RelatedEntry: {
        Data: {
          FromKey: fromKey,
          ToKey: toKey,
          RelationType: relationType,
        },
      },
    };

    return this.http
      .post<Octopus.RelatedEntryResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((response) => {
          if (response.Code === 0 && response.RelatedEntry?.Data) {
            return response.RelatedEntry?.Data;
          }
          return [];
        }),
      );
  }

  batchCreateRelatedEntries(
    entries: { fromKey: string; toKey: string; relationType: string }[],
  ) {
    const request: Octopus.BatchWriteRequest<{
      FromKey: string;
      ToKey: string;
      RelationType: string;
    }> = {};

    entries.forEach((entry, index) => {
      const key = `RelatedEntry.${index + 1}`;

      request[key] = {
        Data: {
          FromKey: entry.fromKey,
          ToKey: entry.toKey,
          RelationType: entry.relationType,
        },
      };
    });

    return this.http
      .post<Octopus.RelatedEntryResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
        { headers: this.getRequestHeaders() },
      )
      .pipe(
        map((response) => {
          if (response.Code === 0 && response.RelatedEntry?.Data) {
            return response.RelatedEntry.Data;
          }
          return [];
        }),
      );
  }

  getRelatedEntryKey(
    fromKey: string,
    toKey: string,
    relationTypes: string[],
  ): Observable<Octopus.RelatedEntry[]> {
    const query: Octopus.SearchQuery<Octopus.RelatedEntry> = {
      $AND: [
        {
          FromKey: {
            $EQ: {
              Body: fromKey,
            },
          },
          ToKey: {
            $EQ: {
              Body: toKey,
            },
          },
        },
        {
          RelationType: {
            $IN: relationTypes,
          },
        },
      ],
    };

    const request: Octopus.RelatedEntryReadRequest = {
      RelatedEntry: {
        Criteria: { SearchQuery: query },
        Scope: {
          Fields: {
            Key: 1,
          },
        },
      },
      Compatibility: { AbEntryKey: '2.0' },
    };

    return this.http
      .post<Octopus.RelatedEntryResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.RelatedEntry?.Data;
          }
          return [];
        }),
      );
  }

  removeRelatedEntry(relationKey: string): Observable<boolean> {
    const request: Octopus.RelatedEntryWriteRequest = {
      RelatedEntry: {
        Data: {
          Key: relationKey,
        },
      },
    };
    return this.http
      .post<Octopus.RelatedEntryResponse>(
        `${this.context.api}${Octopus.Action.DELETE}`,
        request,
      )
      .pipe(
        map(
          (response) => response.Code === Octopus.ResponseStatusCode.Successful,
        ),
      );
  }

  private getRequestHeaders(): { [header: string]: string } {
    return {
      [insightsHeader('entity')]: 'RelatedEntry',
    };
  }
}
